/**
 * 将对象数组根据指定属性分组为二维数组
 * @param {Object[]} array - 要处理的对象数组
 * @param {string} groupKey - 用于分组的属性名
 * @param {Object} [options] - 配置选项
 * @param {string} [options.sort='none'] - 排序方式: 'none', 'asc' (升序), 'desc' (降序)
 * @param {string} [options.format='array'] - 输出格式: 'array' (二维数组), 'arrayWithKey' (带分组键), 'object' (对象)
 * @param {Function} [options.keyTransform] - 对分组键进行转换的函数
 * @returns {Array[]|Object} 分组后的结果（根据format参数决定）
 */

export const  groupBy = (array, groupKey, options = {}) => {
    // 参数验证
    if (!Array.isArray(array)) {
        throw new TypeError('第一个参数必须是数组');
    }
    if (typeof groupKey !== 'string') {
        throw new TypeError('分组键必须是字符串');
    }
    
    // 设置默认选项
    const { 
        sort = 'none', 
        format = 'array', 
        keyTransform = k => k 
    } = options;
    
    // 1. 创建分组映射
    const groupedMap = new Map();
    
    // 2. 遍历数组进行分组
    for (const item of array) {
        // 获取分组键值
        let keyValue = item[groupKey];
        
        // 处理键值为undefined的情况
        if (keyValue === undefined) {
            keyValue = '__undefined__';
        }
        
        // 应用键转换函数
        keyValue = keyTransform(keyValue);
        
        // 创建分组如果不存在
        if (!groupedMap.has(keyValue)) {
            groupedMap.set(keyValue, []);
        }
        
        // 添加当前项到分组
        groupedMap.get(keyValue).push(item);
    }
    
    // 3. 处理排序
    let groups = Array.from(groupedMap);
    
    if (sort !== 'none') {
        groups.sort((a, b) => {
            const aValue = a[0];
            const bValue = b[0];
            
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sort === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // 字符串比较
            const comparison = String(aValue).localeCompare(String(bValue));
            return sort === 'asc' ? comparison : -comparison;
        });
    }
    
    // 4. 根据输出格式处理结果
    switch (format) {
        case 'array':
            return groups.map(group => group[1]);
            
        case 'arrayWithKey':
            return groups.map(([key, value]) => ({
                key: key === '__undefined__' ? undefined : key,
                values: value
            }));
            
        case 'object':
            return groups.reduce((obj, [key, value]) => {
                obj[key === '__undefined__' ? undefined : key] = value;
                return obj;
            }, {});
            
        default:
            throw new Error(`不支持的输出格式: ${format}`);
    }
};

/**
 * 获取当前URL并拼接新参数
 * @param {Object} newParams - 需要拼接的新参数对象（键值对）
 * @param {boolean} [preserveExisting=true] - 是否保留已有参数（默认为true）
 * @param {string} [url] - 可选的自定义URL，默认使用当前页面URL
 * @returns {string} 拼接后的完整URL
 */
export const  getUrlWithParams = (newParams, preserveExisting = true, url) => {
  // 如果未提供URL参数，则使用当前页面URL
  const currentUrl = url || window.location.href;
  
  try {
    // 创建URL对象
    const urlObj = new URL(currentUrl);
    
    // 如果保留已有参数，则合并新参数；否则只使用新参数
    const params = preserveExisting 
      ? new URLSearchParams(urlObj.search)
      : new URLSearchParams();
    
    // 添加新参数（覆盖同名参数）
    for (const [key, value] of Object.entries(newParams)) {
      // 如果值为undefined或null，删除该参数
      if (value === undefined || value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    
    // 更新URL对象的查询参数
    urlObj.search = params.toString();
    
    // 返回完整的URL字符串
    return urlObj.toString();
  } catch (error) {
    console.error("URL处理错误:", error);
    return currentUrl; // 出错时返回原URL
  }
}

// ======= 使用示例 =======

// 示例1: 在当前URL基础上添加新参数，保留原有参数
// const url1 = getUrlWithParams({
//   page: 2,
//   sort: "price"
// });
// console.log("示例1结果:", url1);

// 示例2: 替换所有参数，不保留原有参数
// const url2 = getUrlWithParams({
//   category: "electronics",
//   page: 1
// }, false);
// console.log("示例2结果:", url2);

// 示例3: 使用自定义URL
// const customUrl = "https://example.com/products?category=books";
// const url3 = getUrlWithParams({
//   author: "John Doe",
//   page: 5
// }, true, customUrl);
// console.log("示例3结果:", url3);

// // 示例4: 删除指定参数
// const url4 = getUrlWithParams({
//   page: null, // 删除page参数
//   sort: undefined // 删除sort参数
// });
// console.log("示例4结果:", url4);

// // 示例5: 处理特殊字符和编码
// const url5 = getUrlWithParams({
//   query: "JavaScript 编码测试 & 问题",
//   price: "100$~500$"
// });
// console.log("示例5结果:", url5);
